import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslate } from 'react-admin';

import {
  listReconciliationResolutions,
  listShareAdjustments,
  type ReconciliationResolution,
  type ShareAdjustmentProposal,
} from 'src/pages/admin/services/vaultService';

import { reconciliationErrorMessages } from './helpers';

/**
 * XBIT-4864: one vault's share adjustment proposals and resolved breaks (`GET …/reconciliation/adjustments` and
 * `…/resolutions`). A response that lands after a newer read began (another vault, a refetch) is dropped.
 * TODO(XBIT-4861): the freeze list endpoint lands in BFF round 2; until then the queue is driven by this read plus a vault picker.
 */
export const useReconciliation = (vaultId: string | undefined) => {
  const translate = useTranslate();
  // react-admin's translate is not identity-stable; a ref keeps refetch stable (see useApprovals).
  const translateRef = useRef(translate);
  translateRef.current = translate;
  const latest = useRef(0);
  const shownFor = useRef<string | undefined>();
  const [proposals, setProposals] = useState<ShareAdjustmentProposal[]>([]);
  const [resolutions, setResolutions] = useState<ReconciliationResolution[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const refetch = useCallback(async () => {
    latest.current += 1;
    const call = latest.current;
    if (!vaultId) {
      setProposals([]);
      setResolutions([]);
      setLoadError(null);
      setLoading(false);
      return;
    }
    // Another vault's rows are never shown (or acted on) under this vault while its read is in flight.
    if (shownFor.current !== vaultId) {
      shownFor.current = vaultId;
      setProposals([]);
      setResolutions([]);
    }
    setLoading(true);
    try {
      const [nextProposals, nextResolutions] = await Promise.all([
        listShareAdjustments(vaultId),
        listReconciliationResolutions(vaultId),
      ]);
      if (call !== latest.current) return;
      setProposals(nextProposals);
      setResolutions(nextResolutions);
      setLoadError(null);
    } catch (error) {
      if (call !== latest.current) return;
      setProposals([]);
      setResolutions([]);
      setLoadError(reconciliationErrorMessages(error, translateRef.current)[0]);
    } finally {
      if (call === latest.current) setLoading(false);
    }
  }, [vaultId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { proposals, resolutions, loading, loadError, refetch };
};
